import { ArrowUpRight, Download } from "lucide-react";
import { LEGAL_COMPLIANCE_COPY } from "@/features/legal/config/legal.config";
import { useLegalComplianceDownload } from "@/features/legal/hooks/useLegalComplianceDownload";
import type { LegalCompliance } from "@/features/legal/types/legalCompliance.type";
import { formatLegalComplianceUpdatedAt } from "@/features/legal/utils/formatLegalComplianceUpdatedAt";

type LegalComplianceDocumentCardProps = {
  legalCompliance: LegalCompliance
  onRead: () => void
};

export const LegalComplianceDocumentCard = ({ legalCompliance, onRead }: LegalComplianceDocumentCardProps) => {
  const { download, isDownloading, hasDownloadError } = useLegalComplianceDownload();
  const versionLine = `${LEGAL_COMPLIANCE_COPY.VERSION_PREFIX} ${legalCompliance.version_label} · ${formatLegalComplianceUpdatedAt(legalCompliance.version)}`;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white/70 py-4 pr-3 pl-5 dark:border-gray-700 dark:bg-gray-800/60">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 sm:text-base dark:text-white">{LEGAL_COMPLIANCE_COPY.DOCUMENT_TITLE}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="legal-compliance-version">{versionLine}</p>
        </div>
        <button
          type="button"
          onClick={onRead}
          className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold text-primary hover:bg-primary/10"
          data-testid="legal-compliance-read-button"
        >
          {LEGAL_COMPLIANCE_COPY.READ_DOCUMENT}
          <ArrowUpRight size={16} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => download(legalCompliance)}
          disabled={isDownloading}
          aria-label={LEGAL_COMPLIANCE_COPY.DOWNLOAD}
          title={LEGAL_COMPLIANCE_COPY.DOWNLOAD}
          className="grid size-9 shrink-0 place-items-center rounded-full text-gray-500 hover:bg-primary/10 hover:text-primary disabled:opacity-60 dark:text-gray-400"
          data-testid="legal-compliance-download-button"
        >
          <Download size={16} aria-hidden />
        </button>
      </div>
      {hasDownloadError && <p role="alert" className="text-sm text-error">{LEGAL_COMPLIANCE_COPY.DOWNLOAD_ERROR}</p>}
    </div>
  );
};

LegalComplianceDocumentCard.displayName = "LegalComplianceDocumentCard";
